import { getCurrentInstance, ref } from "vue";
import { styles } from "./styles.config";

import type { SecondaryDataInterface } from "@/types/ContentType";

import { useMainStore } from "@/store/main/index";
import VueGoogleMaps from "@fawmi/vue-google-maps";

export type Marker = {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
};

export const useGoogleMap = (contentData?: SecondaryDataInterface) => {
  const indx = useState<IndxInterface>("data");

  const mainStore = useMainStore();

  const conf = contentData?.conf;
  const data = contentData?.data;

  const API_KEY = indx.value?.siteSettings?.website_google_maps_key;

  // app instance
  const currentInstance = getCurrentInstance();
  const appInstance = currentInstance?.appContext;

  // reactive variables
  const activeMarker = ref<Marker>();
  const mapCenter = ref<Marker["position"]>();
  const mapLoaded = ref(false);

  let MAP_ZOOM_LEVEL;
  const MAP_ZOOM_DEFAULT_LEVEL = 14;

  const mapZoomConfig = conf?.conf?.find((el) => el.startsWith("ZOOM-"));
  const fromCofig = mapZoomConfig?.split("-");

  MAP_ZOOM_LEVEL = fromCofig?.length
    ? Number(fromCofig?.[1])
    : (MAP_ZOOM_LEVEL = MAP_ZOOM_DEFAULT_LEVEL);

  const DEFALUT_LAT = 41.64463817382945;
  const DEFAULT_LNG = 41.626668498551936;

  /**
   * set map center
   */

  // @NOTE: We put map center in hashtag
  const fromHashtag = conf?.hashTag?.split(",")?.map(Number);
  const firstMarkerLocation = data?.list?.[0]?.location;

  const lat =
    fromHashtag?.[0] || Number(firstMarkerLocation?.lat) || DEFALUT_LAT;
  const lng =
    fromHashtag?.[1] || Number(firstMarkerLocation?.lng) || DEFAULT_LNG;

  mapCenter.value = {
    lat,
    lng,
  };

  const MAP_PIN_SVG = new URL("/pin.svg", import.meta?.url).href;
  let pixelOffsets = {
    left: {
      width: 250,
      height: 0,
    },
    right: {
      width: -250,
      height: 200,
    },
  };

  (() => {
    if (mainStore.windowWidth <= mainStore.breakpoints.mobile_sm) {
      pixelOffsets = {
        left: {
          width: 110,
          height: 20,
        },
        right: {
          width: -110,
          height: 140,
        },
      };
      return;
    }
    if (mainStore.windowWidth <= mainStore.breakpoints.desktop_lg) {
      pixelOffsets = {
        left: {
          width: 120,
          height: 150,
        },
        right: {
          width: -120,
          height: 150,
        },
      };
      return;
    }
    if (mainStore.windowWidth <= mainStore.breakpoints.desktop_2k) {
      pixelOffsets = {
        left: {
          width: 150,
          height: 0,
        },
        right: {
          width: -150,
          height: 170,
        },
      };
      return;
    }
  })();

  const mapOptions = {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: true,
    fullscreenControl: false,
    mapTypeId: "roadmap",
    gestureHandling: "cooperative",
    styles,
  };

  // methods
  const attachMapComponent = () => {
    if (!API_KEY) return console.warn("[ GoogleMap ] Can't find API KEY.");

    appInstance?.app.use(VueGoogleMaps, {
      load: {
        key: API_KEY,
      },
      autobindAllEvents: true,
      installComponehandleKeyDownnts: true,
    });

    mapLoaded.value = true;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!event.ctrlKey) return;
    // toggleScroll(true);
  };

  const handleKeyUp = () => {
    // toggleScroll(false);
  };

  const onMarkerClick = (marker: Marker) => {
    mapCenter.value = marker.position;
    activeMarker.value = marker;
  };

  return {
    mapCenter,
    mapOptions,
    mapLoaded,
    MAP_ZOOM_LEVEL,
    MAP_PIN_SVG,
    activeMarker,
    pixelOffsets,
    attachMapComponent,
    handleKeyDown,
    handleKeyUp,
    onMarkerClick,
  };
};
